import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { InvestigationType } from "@/types/db/investigations";
import { investigationService } from "@/services/investigationService";

interface InvestigationEditProviderProps
  extends Omit<EntityEditProviderProps<InvestigationType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function InvestigationEditProvider({
  children,
  entityData,
  loading,
  onSaveSuccess,
}: InvestigationEditProviderProps) {
  const handleSave = async (formData: Partial<InvestigationType>): Promise<InvestigationType> => {
    if (!entityData?.id) {
      throw new Error("No investigation ID available");
    }

    // Strip out read-only fields
    const { id, created_at, tenant_id, ...cleanFormData } = formData;

    // Update the investigation
    const updatedInvestigation = await investigationService.updateInvestigation(entityData.id, cleanFormData);

    // Fetch complete data to return
    const completeInvestigation = await investigationService.getInvestigationById(entityData.id);
    if (!completeInvestigation) {
      throw new Error("Failed to fetch updated investigation data");
    }

    return completeInvestigation;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Investigation"
    >
      {children}
    </EntityEditProvider>
  );
}
